import React, { useMemo, useState, useRef, useEffect } from "react";
import useDrafts from "../hooks/usedrafts";
import { API_BASE, BASELINE_LEAF_NAME, STORAGE_KEY } from "../utils/draftutils";
import { buildBulkPayload } from "../utils/gerarchiautils";

import Toolbar from "../components/gerarchia/toolbar";
import AddChildrenModal from "../components/gerarchia/childrenmodal";
import RootStep from "../components/gerarchia/rootstep";
import TreeCanvas from "../components/gerarchia/treecanvas";

export default function CreaGerarchia() {
  const { drafts, setDrafts, radice, setRadice, hasAnyLeaf } = useDrafts();
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [modal, setModal] = useState({ open: false, parent: null });

  const titleRef = useRef(null);
  const hasRadice = useMemo(() => drafts.some((d) => d.isRadice === true), [drafts]);

  useEffect(() => {
    if (saveMsg) {
      titleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [saveMsg]);

  const apiCheckName = async (nome) => {
  const res = await fetch(`${API_BASE}/api/gerarchia/checkName?nome=${encodeURIComponent(nome)}`, {
    credentials: "include",   // 🔑 serve per autenticazione
  });
  if (!res.ok) throw new Error("Errore durante la verifica");
  const exists = await res.json();
  return !exists; 
};


  const apiBulkSave = async (payload) => {
  const res = await fetch(`${API_BASE}/api/gerarchia/bulk-save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",   // 🔑 aggiunto
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Errore durante il salvataggio");
  }
  return res.json();
};

  
  const addNodeBatch = (nodes) => setDrafts((prev) => [...prev, ...nodes]);

  const removeSubtree = (id) => {
    const toDelete = new Set([id]);
    let changed = true;
    while (changed) {
      changed = false;
      drafts.forEach((d) => {
        if (toDelete.has(d.idPadre) && !toDelete.has(d.tempId)) {
          toDelete.add(d.tempId);
          changed = true;
        }
      });
    }
    const next = drafts.filter((d) => !toDelete.has(d.tempId));
    setDrafts(next);
    if (radice && id === radice.tempId) setRadice(null);
  };

  const openAddChildrenModal = (parent) => setModal({ open: true, parent });
  const closeModal = () => setModal({ open: false, parent: null });
  const onConfirmChildren = (nodes) => { addNodeBatch(nodes); closeModal(); };

  const onRootCreated = (root) => { setDrafts((prev) => [...prev, root]); setRadice(root); };

  const saveGerarchia = async () => {
    setSaveMsg(""); setSaving(true);
    try {
      const payload = buildBulkPayload(drafts, BASELINE_LEAF_NAME);
      await apiBulkSave(payload);
      setSaveMsg("Gerarchia salvata con successo.");
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      window.location.assign("/menuconfig/crea-gerarchia");
    } catch (e) {
      setSaveMsg("Errore nel salvataggio: " + (e?.message || "sconosciuto"));
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 4000);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 ref={titleRef} className="text-xl mb-4 font-bold">CREA GERARCHIA DI SERVIZI</h2>
      <hr className="my-4" />

      {!radice ? (
        <RootStep
          hasRadice={hasRadice}
          onCreated={onRootCreated}
          onValidateName={apiCheckName} 
        />
      ) : (
        <>
          <TreeCanvas
            all={drafts}
            root={radice}
            onAddChildren={openAddChildrenModal}
            onRemove={removeSubtree}
            showConversions={false}
          />
          <div style={{ marginTop: 12 }}>
            <small>Bozze totali: {drafts.length}</small>
          </div>
        </>
      )}

      {modal.open && modal.parent && (
        <AddChildrenModal
          all={drafts}
          parent={modal.parent}
          onClose={closeModal}
          onConfirm={onConfirmChildren}
        />
      )}
      <Toolbar hasAnyLeaf={hasAnyLeaf} onSave={saveGerarchia} saving={saving} saveMsg={saveMsg} />
    </div>
  );

}
